import { z } from 'zod';

// ---------------------------------------------------------------------------
// upload.process.requested — emitted by admin uploader after Storage upload
// ---------------------------------------------------------------------------

export const uploadProcessRequestedSchema = z.object({
  /** Key in wandero bucket, e.g. 'wps/abc-123.jpg' */
  storage_path: z.string().min(1),
  original_filename: z.string().min(1),
  content_type: z.string().min(1),
  size_bytes: z.number().int().positive(),
  category_assignments: z
    .array(
      z.object({
        category_id: z.number().int().positive(),
        is_primary: z.boolean(),
      }),
    )
    .optional(),
  auto_ai_pipeline: z.boolean().default(false),
  lang_code: z.string().min(2).max(5).default('pl'),
  /**
   * If true and a wallpaper already exists at the resolved legacy id (with
   * an image attached), overwrite original_path/metadata and re-run AI. If
   * false (default), the handler errors out with "already exists".
   */
  override: z.boolean().default(false),
});

export type UploadProcessRequestedPayload = z.infer<typeof uploadProcessRequestedSchema>;

// ---------------------------------------------------------------------------
// upload.processed — emitted by worker handler on success or error
// ---------------------------------------------------------------------------

export const uploadProcessedSuccessSchema = z.object({
  wallpaper_id: z.number().int().positive(),
  original_path: z.string(),
  width_px: z.number().int().positive(),
  height_px: z.number().int().positive(),
  /** Strict hex '#RRGGBB' */
  dominant_color: z.string().regex(/^#[0-9a-f]{6}$/i),
  ai_pipeline_event_ids: z.array(z.number()).optional(),
  /**
   * True when the upload replaced an existing placeholder row (legacy id
   * reserved without an image). Absent on a fresh insert.
   */
  overrode_placeholder: z.boolean().optional(),
});

export const uploadProcessedErrorSchema = z.object({
  error: z.string(),
  storage_path: z.string(),
});

export const uploadProcessedSchema = z.union([
  uploadProcessedSuccessSchema,
  uploadProcessedErrorSchema,
]);

export type UploadProcessedPayload = z.infer<typeof uploadProcessedSchema>;
